import React, { useEffect, useState } from 'react';
import { get, isEmpty } from 'lodash';
import { Field, change } from 'redux-form';
import { useSelector, useDispatch } from 'react-redux';
import RenderSelect from '../RenderSelect';
import PlazaController from '../../../controllers/plaza/PlazaController';
import { fullPlazaNameLabel } from '../../../constants/naming';
import { mockedPlazaList } from '../../../../screens/Reports/components/DeliveredEnergy/mockedData';
import { isDemo } from '../../Echarts/helpers/helper';
import { isDirectlySupervisor } from '../../../services/HasAccess';
import { getCustomerId, getUser } from '../../../../screens/Login/selectors';
import { selectActiveCustomerId, selectFormFieldValue } from '../../../../screens/Reports/selectors';
import { noPlazaOptions } from '../../../constants/CommonUsedDropdownsStatics';

export const getFilteredPlazasOptions = async (showHidden = false) => {
  try {
    const plazas = isDemo() ? mockedPlazaList.payloads : await PlazaController.getAll({ page_token: 0, page_size: 999 }, { showHidden });
    const plazaList = PlazaController.sortByName(plazas).filter(({ chargerCount }) => chargerCount >= 1);
    const options = PlazaController.makeOptionsWithStatus(plazaList);
    const plazaUuid = options.length > 0 ? get(options, '0.value', null) : null;

    return {
      plazas: options,
      plazaUuid,
    };
  } catch (e) {
    console.error(e);

    return {
      plazas: [],
      plazaUuid: null,
    };
  }
};

/**
 * Filters plazas by customer ID. Plazas without an explicit customerId
 * are treated as belonging to the domestic customer (backward compatibility).
 */
const filterPlazasByCustomer = (plazas, targetCustomerId, domesticCustomerId) =>
  plazas.filter((plaza) => {
    const plazaCustomerId = plaza.customerId || domesticCustomerId;

    return String(plazaCustomerId) === String(targetCustomerId);
  });

/**
 * When `selectFirstByDefault` is true, ensures the form always has a valid plaza selected.
 * - If the current selection is not in the filtered list → selects the first available plaza.
 * - If the filtered list is empty → clears the selection.
 */
const useAutoDefaultPlaza = ({ enabled, formName, fieldName, filteredOptions, selectedValue, dispatch }) => {
  useEffect(() => {
    if (!enabled) return;

    if (!isEmpty(filteredOptions)) {
      const isValid = filteredOptions.some((option) => option.value === selectedValue);

      if (!isValid) {
        dispatch(change(formName, fieldName, filteredOptions[0].value));
      }
    } else if (!isEmpty(selectedValue)) {
      dispatch(change(formName, fieldName, null));
    }
  }, [enabled, formName, fieldName, filteredOptions, selectedValue, dispatch]);
};

const RenderPlazaSelectComponent = (props) => {
  const [allPlazas, setAllPlazas] = useState([]);
  const dispatch = useDispatch();

  // Fetch all plazas on mount; they are filtered client-side based on the selected customer.
  useEffect(() => {
    getFilteredPlazasOptions(props.showHidden).then(({ plazas }) => {
      setAllPlazas(plazas);
    });
  }, [props.showHidden]);

  // Resolve which customer's plazas to display.
  const user = useSelector(getUser);
  const isSupervisor = isDirectlySupervisor(user);
  const domesticCustomerId = useSelector(getCustomerId);
  const selectedCustomerId = useSelector(selectActiveCustomerId);
  const targetCustomerId = isSupervisor && selectedCustomerId ? selectedCustomerId : domesticCustomerId;

  // Filter plazas and build display options.
  const filteredOptions = filterPlazasByCustomer(allPlazas, targetCustomerId, domesticCustomerId);
  const hasPlazas = filteredOptions.length > 0;
  const displayOptions = hasPlazas ? filteredOptions : noPlazaOptions;

  // Determine which redux-form instance owns this field.
  // TODO: Extract this logic into a shared helper for all form fields that need to know their form name.
  const formName = props.meta?.form || (props.name === 'plazaUuid' ? 'plazaReport' : 'reportFilterForm');
  const selectedPlazaUuid = useSelector((state) => selectFormFieldValue(state, formName, props.name));

  // Auto-select the first plaza when the current selection becomes invalid (e.g. after customer change).
  useAutoDefaultPlaza({
    enabled: props.selectFirstByDefault,
    formName,
    fieldName: props.name,
    filteredOptions,
    selectedValue: selectedPlazaUuid,
    dispatch,
  });

  return (
    <Field
      key={`${props.name}_name`}
      name={props.name}
      component={RenderSelect}
      floatingLabelText={fullPlazaNameLabel}
      className={props.className}
      fullWidth
      options={displayOptions}
      disabled={!hasPlazas || props.disabled}
      multiple={props.multiple}
      showCheckboxes={props.showCheckboxes}
      clearable={!props.selectFirstByDefault}
      withSelectAll={props.multiple}
      isEmptyByDefault
    />
  );
};

export default RenderPlazaSelectComponent;
